<template>
  <popup @close-popup="closePopup">
    <template v-slot:content>
      <form class="login" @submit.prevent="loginUser">
        <div class="login-container">
          <div class="login-container__header">
            <span class="title">Sign in</span>
            <span class="subtitle">
              Don't have an account?
              <a @click="fireClick" :href="createAccountLink !== 'unnknown' ? createAccountLink : '#'" class="create-account">
                Create account
                <span v-if="!createAccountLink" class="create-account__tooltip"
                  >Download the Carbon mobile application on your device to enjoy Carbon Zero everywhere</span
                >
              </a>
            </span>
          </div>
          <div class="login-container__content">
            <label>
              <span class="login-container__content-title">Phone number</span>
              <div class="login-container__content__phone-number">
                <div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_2415_8839)">
                      <path
                        d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
                        fill="#F0F0F0"
                      />
                      <path
                        d="M-0.000366211 12.0001C-0.000366211 17.1598 3.25613 21.5582 7.82574 23.2538V0.746582C3.25613 2.44205 -0.000366211 6.84061 -0.000366211 12.0001Z"
                        fill="#6DA544"
                      />
                      <path
                        d="M23.9999 12.0001C23.9999 6.84061 20.7434 2.44205 16.1738 0.746582V23.2538C20.7434 21.5582 23.9999 17.1598 23.9999 12.0001Z"
                        fill="#6DA544"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2415_8839">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <select name="code" id="code" v-model="countryCode" class="login__select">
                    <option value="+234">+234</option>
                  </select>
                </div>
                <input placeholder="(806) 000-0000" type="tel" v-model="userPhoneNumber" class="input" v-mask="'(###) ###-####'" />
              </div>
            </label>
            <label>
              <span class="login-container__content-title">Pin</span>
              <div class="login-container__content__pin">
                <input
                  placeholder="Enter your pin"
                  v-model="pin"
                  class="input"
                  :type="showPin ? 'text' : 'password'"
                  v-mask="showPin ? '# # # #' : '####'"
                />
                <img class="nav-brand__logo" @click="setShowPin" v-if="!showPin" :src="Hide" alt="Hide Password" />
                <img class="nav-brand__logo" @click="setShowPin" v-else :src="Show" alt="Show Password" />
              </div>
            </label>
            <div v-if="store.state.errorMessage" class="login-container__error-message message message--error">
              {{ store.state.errorMessage }}
            </div>
            <div class="login-container__content__forgotPassword">
              <a :href="customerPortal + '/user/forgotpin'" target="_blanky"> Forgot pin </a>
            </div>
            <div class="btn btn__primary btn--lg">
              <button>Sign in</button>
            </div>
          </div>
        </div>
      </form>
    </template>
  </popup>
</template>

<script lang="ts" setup>
import { ref, computed, reactive, onBeforeMount } from 'vue'
import { createDeviceDetector } from 'next-vue-device-detector'

import { LoginParameterInterface, MerchantItems } from '@/models/composables/index'
import store from '@/composables/useZeroWidgetStore/methods'
import { required, helpers } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { messagesConstants } from '@/constants'
import Hide from '@/assets/icons/Hide.svg'
import Show from '@/assets/icons/Show.svg'
import { mask } from 'vue-the-mask'
import Popup from '@/components/cards/popup/index.vue'

const props = defineProps<{
  totalPrice: number
  purchaseRefId: string
  purchaseItems: Array<MerchantItems>
}>()

const pin = ref('')
const userPhoneNumber = ref('')
const countryCode = ref('+234')

const customerPortal = computed(() => store.state.customerPortaAddress)

const showPin = ref(false)
const setShowPin = () => (showPin.value = !showPin.value)
const validPassword = helpers.regex(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/)

const errorMessages = reactive({
  userPhoneNumber: '',
  pin: ''
})

const vMask = mask

enum StoreLink {
  ios = 'https://apps.apple.com/ng/app/carbon-secure-banking-loans/id1494856668',
  android = 'https://play.google.com/store/apps/details?id=com.lenddo.mobile.paylater',
  unknown = 'unknown'
}

const createAccountLink = ref('')

const determineStore = () => {
  const device = createDeviceDetector()
  if (device.ios) {
    return StoreLink.ios
  }
  if (device.android) {
    return StoreLink.android
  }

  return StoreLink.unknown
}

const fireClick = (e: Event) => {
  if (!createAccountLink.value) {
    e.preventDefault()
    return
  }
}

onBeforeMount(() => {
  const link = determineStore()
  if (link === StoreLink.unknown) {
    createAccountLink.value = ''
  } else {
    createAccountLink.value = link
  }
})

const rules: any = computed(() => ({
  userPhoneNumber: {
    required: helpers.withMessage(messagesConstants.NO_EMPTY_EMAIL_MESSAGE, required)
  },
  pin: {
    required: helpers.withMessage(messagesConstants.NO_EMPTY_PASSWORD_MESSAGE, required),
    validPassword: helpers.withMessage(messagesConstants.PASSWORD_VALIDATION_MESSAGE, validPassword)
  }
}))
const v$ = useVuelidate(rules, {
  userPhoneNumber,
  pin
})

const setValidationErrorMessage = () => {
  errorMessages.userPhoneNumber = v$.value.userPhoneNumber.$error ? `${v$.value.userPhoneNumber.$errors[0].$message}` : ''
  errorMessages.pin = v$.value.pin.$error ? `${v$.value.pin.$errors[0].$message}` : ''
}

const loginUser = () => {
  setValidationErrorMessage()
  const mobileNumber = countryCode.value + userPhoneNumber.value.replace(/[^0-9]/g, '')
  const enteredPin = pin.value.replace(' ', '')
  const payload: LoginParameterInterface = {
    pin: enteredPin,
    phonenumber: mobileNumber,
    totalPrice: Number(props.totalPrice),
    purchaseRefId: props.purchaseRefId,
    purchaseItems: props.purchaseItems
  }

  store.actions.login(payload)
}

const emit = defineEmits<{
  (e: 'close-popup'): void
}>()
const closePopup = () => {
  return emit('close-popup')
}
</script>
